import { type FC, type ReactNode, useMemo } from 'react';
import { ItemPrice } from './ItemPrice';
import { cn } from '../utils/cn';
import { parseItemDetails, abbreviateWearCondition } from '../utils/formatters';

interface PrizeNamePriceRowProps {
  /** hashName предмета. Разбирается здесь, чтобы разбор не копировался по карточкам. */
  hashName: string;
  /** Имя из API — показываем, если разбор не дал ни скина, ни оружия. */
  fallbackName: string;
  /** Цена в монетах. */
  credits: number;
  /** Цена Steam. У `DrawCard` такого поля нет вовсе — тогда просто не передаётся,
   *  и `ItemPrice` рисует одни монеты. */
  steamPrice?: number | null;
  /** Мобильная теснее десктопной: имя крупнее и обрезается, строка цены ближе и жирнее.
   *  `compact` — для строк списка, где под текст остаётся ~190px и крупный кегль режет
   *  имя скина уже на «Legion of Anubis» (анимация раунда баттла). */
  layout: 'mobile' | 'desktop' | 'compact';
  /** Подпись перед ценой («за»). Есть у `DailyGiveawayCard` и `DrawCard`, нет у `RaffleCard`. */
  priceCaption?: ReactNode;
  /** ★ / StatTrak™ / Souvenir в строке оружия. Сегодня их показывает только `DrawCard`;
   *  по умолчанию выключено, чтобы вынос не поменял вид двух других карточек молча. */
  showQualityBadges?: boolean;
  /** Отступы и позиционирование от вызывающей карточки. */
  className?: string;
}

/**
 * Строка приза: «оружие + состояние» → название скина → цена.
 *
 * Вынесено в #544 из `DailyGiveawayCard`, `DrawCard` и `RaffleCard`, где этот блок был
 * написан шесть раз (три карточки × две раскладки). Именно здесь жил дефект #532: имя и
 * цена делили одну строку по ширине, а цена не сжималась, из-за чего резалось имя. Правка
 * #532 переехала сюда целиком и теперь одна на всех:
 *
 *  - цена стоит **отдельной строкой** под названием, а не в одном ряду с ним;
 *  - `truncate` висит на самом `<span>` с именем оружия, а не на flex-контейнере
 *    (на flex-боксе `text-overflow: ellipsis` не работает — режет без многоточия);
 *  - `min-w-0` разрешает имени сжиматься внутри flex-строки, `shrink-0` не даёт потерять
 *    бейдж состояния, который короткий и подсказывает точный вариант предмета.
 */
export const PrizeNamePriceRow: FC<PrizeNamePriceRowProps> = ({
  hashName,
  fallbackName,
  credits,
  steamPrice,
  layout,
  priceCaption,
  showQualityBadges = false,
  className,
}) => {
  const parsed = useMemo(() => parseItemDetails(hashName), [hashName]);

  // У граффити и стикеров в поле состояния лежит цвет, а не износ, — сокращать его нечем.
  const isGraffitiOrSticker = parsed.weaponName === 'Graffiti' || parsed.weaponName === 'Sticker';
  const conditionLabel = parsed.condition
    ? isGraffitiOrSticker
      ? parsed.condition
      : abbreviateWearCondition(parsed.condition)
    : '';

  const isMobile = layout === 'mobile';
  const isCompact = layout === 'compact';
  const badges = showQualityBadges && (parsed.isStar || parsed.isStatTrak || parsed.isSouvenir);
  const hasTopRow = Boolean(parsed.weaponName || conditionLabel || badges);

  return (
    <div className={cn('flex flex-col', className)}>
      {hasTopRow && (
        <div className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase leading-4 tracking-[1px] text-white/80">
          {parsed.weaponName && <span className="min-w-0 truncate">{parsed.weaponName}</span>}
          {showQualityBadges && parsed.isStar && <span className="shrink-0 text-amber-200">★</span>}
          {showQualityBadges && parsed.isStatTrak && <span className="shrink-0 text-amber-200">StatTrak™</span>}
          {showQualityBadges && parsed.isSouvenir && <span className="shrink-0 text-amber-200">Souvenir</span>}
          {conditionLabel && <span className="shrink-0 text-white">{conditionLabel}</span>}
        </div>
      )}

      <h3
        className={cn(
          'm-0 truncate font-bold tracking-[-0.4px] text-white',
          isCompact ? 'text-base leading-6' : 'leading-7',
          isMobile ? 'text-2xl' : isCompact ? '' : 'text-xl',
        )}
      >
        {parsed.skinName || parsed.weaponName || fallbackName}
      </h3>

      <div
        className={cn(
          'flex items-center whitespace-nowrap text-white',
          isCompact ? 'gap-1 text-sm font-semibold leading-5' : 'leading-7',
          isMobile ? 'mt-1 gap-1 text-lg font-bold' : isCompact ? '' : 'mt-1.5 gap-1.5 text-base font-semibold',
        )}
      >
        {priceCaption && <span className="text-xs font-normal text-white/80">{priceCaption}</span>}
        <ItemPrice
          credits={credits}
          steamPrice={steamPrice}
          creditsIconSize={isMobile ? 16 : isCompact ? 13 : 14}
          className={isMobile || isCompact ? undefined : 'gap-1.5'}
        />
      </div>
    </div>
  );
};
